/**
 * E9. Монета: в конверте с 2009 года, с 18 лет. Ожидание +3 % в год при
 * σ 80 % (логнормальный рост считает движок раз в год, `serviceHoldings`).
 * Курс показывается только по тапу (`{ view: true }`), и каждый просмотр —
 * тяга +2: так игра отличает инвестицию от азарта поведением.
 * `{ amount }` докупает (не больше денег), `{ sell: true }` продаёт всю
 * позицию по текущей стоимости (`flags.cryptoHeld`). «Пропустить» — ничего.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { CRYPTO_MEAN, CRYPTO_SIGMA, FLAG_CRYPTO_HELD, type Life, type MinigameOutcome } from '@life/engine';
import type { ExpectancyText, MinigameDef } from '../types';
import { clampBet, noPlay } from '../casino/common';
import { ADULT_AGE, heldOf } from './common';

export const CRYPTO_FIRST_YEAR = 2009;
export const CRYPTO_VIEW_URGE = 2;

export interface CryptoState {
  /** Текущая стоимость позиции (0, если монеты нет). */
  held: number;
  maxAmount: number;
  meanGain: number;
  sigma: number;
}

export type CryptoInput = { amount: number } | { view: true } | { sell: true };

export const crypto: MinigameDef<CryptoInput, CryptoState> = {
  id: 'crypto',
  section: 'E',
  triggers: [{ kind: 'envelope' }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    if (life.age < ADULT_AGE || life.birthYear + life.age < CRYPTO_FIRST_YEAR) return false;
    return life.money > 0 || heldOf(life, FLAG_CRYPTO_HELD) > 0;
  },
  setup(life: Life): CryptoState {
    return { held: heldOf(life, FLAG_CRYPTO_HELD), maxAmount: Math.max(0, life.money), meanGain: CRYPTO_MEAN - 1, sigma: CRYPTO_SIGMA };
  },
  play(state: CryptoState, input: CryptoInput, life: Life): MinigameOutcome {
    if ('view' in input) return { moneyDelta: 0, urgeDelta: CRYPTO_VIEW_URGE };
    const held = heldOf(life, FLAG_CRYPTO_HELD);
    if ('sell' in input) {
      if (held <= 0) return noPlay();
      return { moneyDelta: held, urgeDelta: 0, flags: { [FLAG_CRYPTO_HELD]: 0 }, logKey: 'minigame.crypto.sell', logParams: { amount: Math.round(held) } };
    }
    const amount = clampBet(Math.min(input.amount, state.maxAmount), life.money);
    if (amount <= 0) return noPlay();
    return {
      moneyDelta: -amount,
      urgeDelta: 0,
      flags: { [FLAG_CRYPTO_HELD]: held + amount },
      logKey: 'minigame.crypto.buy',
      logParams: { amount: Math.round(amount) },
    };
  },
  expected(): MinigameOutcome {
    return noPlay();
  },
  expectancyText: (state: CryptoState): ExpectancyText => ({
    key: 'minigame.expect.crypto',
    params: { gain: Math.round(state.meanGain * 100), sigma: Math.round(state.sigma * 100), urge: CRYPTO_VIEW_URGE },
  }),
};
